/**
 * Wdrożenie — od pustego HQ do liczenia prawdziwych pieniędzy.
 *
 * Ta strona istnieje, bo lista kontrolna z dokumentacji odhaczana w głowie
 * zawsze kończy się tak samo: ktoś zapomina o katalogu planów albo o nocnej
 * synchronizacji i po miesiącu MRR nie zgadza się z CRM-em. Tutaj każdy punkt
 * jest sprawdzany w bazie.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Card, Chip, Empty, ErrorState, Loading } from "../components/ui";
import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { ago, LABELS } from "../lib/format";
import type { ApiKeyRow, IssuedKey, PortfolioTag, ProductBrief, Readiness } from "../lib/types";

const PURGE_PHRASE = "USUŃ DANE DEMO";

export function DeploymentPage() {
  const queryClient = useQueryClient();
  const { includeDemo, setIncludeDemo } = useDemo();

  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });

  const demoProducts = products.data?.products.filter((p) => p.is_demo) ?? [];
  const realProducts = products.data?.products.filter((p) => !p.is_demo) ?? [];

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Konfiguracja</p>
          <h1>Wdrożenie</h1>
          <p className="page-head__sub">
            Co jeszcze dzieli każdy produkt od liczenia prawdziwych pieniędzy.
            Punkty są sprawdzane w bazie, nie odhaczane ręcznie.
          </p>
        </div>
      </header>

      <div className="stack">
        {demoProducts.length > 0 && (
          <DemoPanel
            products={demoProducts}
            hasReal={realProducts.length > 0}
            includeDemo={includeDemo}
            onToggle={setIncludeDemo}
            onPurged={() => {
              void queryClient.invalidateQueries();
            }}
          />
        )}

        <NewProductCard onCreated={() => void queryClient.invalidateQueries({ queryKey: ["products"] })} />

        <PortfolioTags />

        {products.isPending ? (
          <Loading />
        ) : products.isError ? (
          <ErrorState error={products.error} />
        ) : (
          products.data?.products.map((product) => (
            <ProductSetup key={product.slug} product={product} />
          ))
        )}
      </div>
    </>
  );
}

/* ── Dane demonstracyjne ──────────────────────────────────────────────────── */

function DemoPanel({
  products,
  hasReal,
  includeDemo,
  onToggle,
  onPurged,
}: {
  products: ProductBrief[];
  hasReal: boolean;
  includeDemo: boolean;
  onToggle: (value: boolean) => void;
  onPurged: () => void;
}) {
  const [phrase, setPhrase] = useState("");

  const purge = useMutation({
    mutationFn: () =>
      api.post<{ removed: string[]; count: number }>("/maintenance/purge-demo", {
        confirm: PURGE_PHRASE,
      }),
    onSuccess: () => {
      setPhrase("");
      onPurged();
    },
  });

  return (
    <Card
      title="Dane demonstracyjne"
      note={`${products.length} produkty wygenerowane poleceniem seed-demo`}
      action={<Chip tone="warn">demo</Chip>}
    >
      <p className="muted">
        Zostają, dopóki są potrzebne — służą do pokazania systemu komuś, kto go
        jeszcze nie zna. Przełącznik poniżej odcina je od wszystkich liczb
        w portfolio, nie kasując niczego.
      </p>

      <ul className="taglist">
        {products.map((product) => (
          <li key={product.slug}>
            <Link to={`/produkt/${product.slug}`}>{product.name}</Link>
          </li>
        ))}
      </ul>

      <label className="switch switch--inline">
        <input
          type="checkbox"
          checked={includeDemo}
          onChange={(event) => onToggle(event.target.checked)}
        />
        <span className="switch__track" aria-hidden="true">
          <span className="switch__knob" />
        </span>
        <span className="switch__label">
          {includeDemo
            ? "Dane demo wliczają się do portfolio"
            : "Dane demo są odcięte od liczb"}
        </span>
      </label>

      <div className="danger">
        <h3>Usunięcie danych demonstracyjnych</h3>
        <p>
          {hasReal
            ? "Masz już prawdziwe produkty. Demo przestaje pomagać, a zaczyna zaciemniać raporty."
            : "Zrób to dopiero wtedy, gdy podepniesz pierwszy prawdziwy produkt — inaczej HQ zostanie puste."}{" "}
          Kasujemy produkty demo razem z klientami, subskrypcjami, historią MRR,
          snapshotami i alertami. Nie da się tego cofnąć.
        </p>
        <div className="danger__row">
          <input
            aria-label={`Wpisz „${PURGE_PHRASE}", aby potwierdzić`}
            placeholder={PURGE_PHRASE}
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
          />
          <button
            type="button"
            className="btn btn--danger"
            disabled={phrase !== PURGE_PHRASE || purge.isPending}
            onClick={() => purge.mutate()}
          >
            {purge.isPending ? "Usuwam…" : "Usuń dane demo"}
          </button>
        </div>
        {purge.isError && (
          <p className="form-error" role="alert">
            {purge.error instanceof Error ? purge.error.message : "Nie udało się usunąć"}
          </p>
        )}
        {purge.isSuccess && (
          <p className="muted" style={{ marginTop: "var(--space-3)" }}>
            Usunięto: {purge.data.removed.join(", ")}.
          </p>
        )}
      </div>
    </Card>
  );
}

/* ── Nowy produkt ─────────────────────────────────────────────────────────── */

function NewProductCard({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    name: "",
    tagline: "",
    default_currency: "PLN",
    activation_event: "",
    gsc_site_url: "",
    trial_length_days: "14",
    accounts: true,
    trials: true,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/products", {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        tagline: form.tagline.trim() || undefined,
        gsc_site_url: form.gsc_site_url.trim() || undefined,
        default_currency: form.default_currency,
        activation_event: form.activation_event.trim().toUpperCase() || undefined,
        trial_length_days: Number(form.trial_length_days) || 14,
        status: "building",
        capabilities: { accounts: form.accounts, trials: form.trials },
      }),
    onSuccess: () => {
      setForm({ ...form, slug: "", name: "", tagline: "", activation_event: "" });
      setOpen(false);
      onCreated();
    },
  });

  const set = (field: keyof typeof form) => (value: string | boolean) =>
    setForm((current) => ({ ...current, [field]: value }));

  return (
    <Card
      title="Nowy produkt"
      note="Pierwszy krok integracji prawdziwego CRM-a"
      action={
        <button type="button" className="btn" onClick={() => setOpen((value) => !value)}>
          {open ? "Zwiń" : "Dodaj produkt"}
        </button>
      }
    >
      {!open ? (
        <p className="muted">
          Produkt to jedna aplikacja: własna baza, własne konto u operatora
          płatności, własny klucz API. Po dodaniu wygeneruj klucz i wklej go do
          konfiguracji CRM-a — reszta dzieje się po jego stronie.
        </p>
      ) : (
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <label>
            <span>Slug (w adresach i kluczach API)</span>
            <input
              required
              pattern="[a-z0-9-]+"
              placeholder="asenizacja"
              value={form.slug}
              onChange={(event) => set("slug")(event.target.value)}
            />
          </label>
          <label>
            <span>Nazwa</span>
            <input
              required
              placeholder="CRM Asenizacja"
              value={form.name}
              onChange={(event) => set("name")(event.target.value)}
            />
          </label>
          <label className="form-grid__wide">
            <span>Jednym zdaniem</span>
            <input
              placeholder="Trasy, zlecenia i rozliczenia dla firm asenizacyjnych"
              value={form.tagline}
              onChange={(event) => set("tagline")(event.target.value)}
            />
          </label>
          <label>
            <span>Waluta</span>
            <input
              required
              maxLength={3}
              value={form.default_currency}
              onChange={(event) => set("default_currency")(event.target.value.toUpperCase())}
            />
          </label>
          <label>
            <span>Długość triala (dni)</span>
            <input
              type="number"
              min={0}
              value={form.trial_length_days}
              onChange={(event) => set("trial_length_days")(event.target.value)}
            />
          </label>
          <label className="form-grid__wide">
            <span>Zdarzenie aktywacji</span>
            <input
              placeholder="FIRST_ROUTE_CREATED"
              value={form.activation_event}
              onChange={(event) => set("activation_event")(event.target.value)}
            />
            <small>
              Moment, w którym użytkownik rozumie, po co ten produkt jest. Bez
              niego wskaźnik aktywacji nie istnieje.
            </small>
          </label>
          <label className="form-grid__wide">
            <span>Witryna w Search Console</span>
            <input
              placeholder="sc-domain:kurniki.online"
              value={form.gsc_site_url}
              onChange={(event) => set("gsc_site_url")(event.target.value)}
            />
            <small>
              Skopiuj dokładnie z paska adresu Search Console.{" "}
              <code>sc-domain:</code> obejmuje całą domenę z subdomenami,{" "}
              <code>https://</code> tylko ten jeden prefiks — i po cichu pominie
              ruch z <code>www</code>. Puste = tego produktu nie pobieramy.
            </small>
          </label>

          <fieldset className="form-grid__wide">
            <legend>Co produkt potrafi</legend>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.accounts}
                onChange={(event) => set("accounts")(event.target.checked)}
              />
              <span>
                Rozróżnia konto od użytkownika. Odznacz przy produkcie
                jednoosobowym — wtedy konto mapuje się na użytkownika jako
                <code> u:&lt;id&gt;</code>.
              </span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.trials}
                onChange={(event) => set("trials")(event.target.checked)}
              />
              <span>Ma okres próbny.</span>
            </label>
          </fieldset>

          {create.isError && (
            <p className="form-error form-grid__wide" role="alert">
              {create.error instanceof Error ? create.error.message : "Nie udało się dodać"}
            </p>
          )}

          <div className="form-grid__wide">
            <button className="btn btn--primary" type="submit" disabled={create.isPending}>
              {create.isPending ? "Dodaję…" : "Dodaj produkt"}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

/* ── Gotowość produktu ────────────────────────────────────────────────────── */

const FUNNEL_STEPS: { key: string; label: string; placeholder: string; help: string }[] = [
  {
    key: "demo",
    label: "Demo",
    placeholder: "/demo",
    help: "Strona, na której ktoś ogląda produkt przed założeniem konta.",
  },
  {
    key: "signup",
    label: "Rejestracja",
    placeholder: "/rejestracja",
    help: "Formularz zakładania konta — krok tuż przed rejestracją.",
  },
];

/**
 * Adresy kroków pośrednich lejka.
 *
 * Bez nich lejek ma cztery etapy zamiast sześciu i nie odpowiada na pytanie
 * „ilu ludzi weszło na demo, a ilu doszło do formularza". HQ nie zgaduje
 * tych adresów po nazwie ścieżki: pierwszy wpis na blogu o tytule zawierającym
 * słowo „demo" zafałszowałby cały krok.
 *
 * Kilka adresów na krok oddziela się przecinkiem — produkt może mieć demo
 * pod dwoma ścieżkami po zmianie nazewnictwa i oba są tym samym krokiem.
 */
function FunnelPages({ product, onSaved }: { product: ProductBrief; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      FUNNEL_STEPS.map((step) => [step.key, (product.funnel_pages?.[step.key] ?? []).join(", ")]),
    ),
  );

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/products/${product.slug}`, {
        funnel_pages: Object.fromEntries(
          FUNNEL_STEPS.map((step) => [
            step.key,
            (draft[step.key] ?? "")
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
          ]).filter(([, paths]) => (paths as string[]).length),
        ),
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="keys">
      <div className="keys__head">
        <h3>Adresy lejka</h3>
        <button
          type="button"
          className="btn"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Zapisuję…" : "Zapisz adresy"}
        </button>
      </div>

      <div className="funnel-pages">
        {FUNNEL_STEPS.map((step) => (
          <label key={step.key} className="funnel-pages__field">
            <span className="funnel-pages__label">{step.label}</span>
            <input
              type="text"
              value={draft[step.key] ?? ""}
              placeholder={step.placeholder}
              onChange={(event) =>
                setDraft((previous) => ({ ...previous, [step.key]: event.target.value }))
              }
            />
            <span className="funnel-pages__help">{step.help}</span>
          </label>
        ))}
      </div>

      <p className="funnel-pages__note">
        Kilka adresów oddziel przecinkiem. Dopasowanie jest prefiksowe na granicy
        segmentu — <span className="mono">/demo</span> obejmie{" "}
        <span className="mono">/demo/kurnik</span>, ale nie{" "}
        <span className="mono">/demonstracja</span>. Ruch na tych adresach musi
        przychodzić przez <span className="mono">POST /api/v1/metrics/pages</span>.
      </p>

      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}

/**
 * Projekt Microsoft Clarity — mapy kliknięć, przewijania i nagrania wizyt.
 *
 * To pole wygląda jak `ga4_property_id` i `gsc_site_url`, a zachowuje się
 * inaczej, więc warto wiedzieć, na czym polega różnica. Tamte dwa mówią
 * nocnemu kolektorowi, skąd pobrać liczby. To nie uruchamia żadnego pobierania:
 * HQ porównuje produkty agregatami, a Clarity odpowiada na pytania o pojedyncze
 * strony i pojedyncze wizyty — czyli dokładnie to, co słownik metryk zostawia
 * narzędziu źródłowemu.
 *
 * Po co więc jest w HQ: żeby lista kontrolna wdrożenia sprawdzała obecność
 * pomiaru zachowania tak samo jak obecność klucza API, i żeby z portfolio dało
 * się wejść do właściwego projektu bez szukania go po nazwie.
 */
function ClarityProject({ product, onSaved }: { product: ProductBrief; onSaved: () => void }) {
  const [draft, setDraft] = useState(product.clarity_project_id ?? "");
  const zapisany = product.clarity_project_id;

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/products/${product.slug}`, {
        clarity_project_id: draft.trim().toLowerCase() || null,
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="keys">
      <div className="keys__head">
        <h3>Microsoft Clarity</h3>
        <button
          type="button"
          className="btn"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Zapisuję…" : "Zapisz projekt"}
        </button>
      </div>

      <div className="funnel-pages">
        <label className="funnel-pages__field">
          <span className="funnel-pages__label">Identyfikator projektu</span>
          <input
            type="text"
            value={draft}
            placeholder="3t0wlogvdz"
            onChange={(event) => setDraft(event.target.value)}
          />
          <span className="funnel-pages__help">
            Clarity → Ustawienia → Setup. Same małe litery i cyfry — nie adres
            projektu i nie gotowy fragment <span className="mono">&lt;script&gt;</span>.
          </span>
        </label>
      </div>

      <p className="funnel-pages__note">
        HQ nie pobiera stąd żadnych liczb i nie pilnuje świeżości tego źródła.
        Wpis mówi portfolio, że produkt mierzy zachowanie na stronie, i odhacza
        pozycję na liście powyżej. Po stronie produktu zostają dwie rzeczy, których
        to pole nie załatwia: znacznik ma się wczytywać dopiero po zgodzie na
        ciasteczka, a część zalogowana musi mieć{" "}
        <span className="mono">data-clarity-mask</span>.
        {zapisany && (
          <>
            {" "}
            <a
              href={`https://clarity.microsoft.com/projects/view/${zapisany}`}
              target="_blank"
              rel="noreferrer"
            >
              Otwórz projekt w Clarity
            </a>
            .
          </>
        )}
      </p>

      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}

function ProductSetup({ product }: { product: ProductBrief }) {
  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const readiness = useQuery({
    queryKey: ["readiness", product.slug],
    queryFn: () => api.get<Readiness>(`/products/${product.slug}/readiness`),
  });

  const keys = useQuery({
    queryKey: ["keys", product.slug],
    queryFn: () => api.get<{ keys: ApiKeyRow[] }>(`/products/${product.slug}/keys`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["keys", product.slug] });
    void queryClient.invalidateQueries({ queryKey: ["readiness", product.slug] });
    void queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const issue = useMutation({
    mutationFn: (environment: string) =>
      api.post<IssuedKey>(`/products/${product.slug}/keys`, { environment }),
    onSuccess: (data) => {
      setIssued(data);
      refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/products/${product.slug}/keys/${id}`),
    onSuccess: refresh,
  });

  const activeKeys = keys.data?.keys.filter((key) => !key.revoked_at) ?? [];

  return (
    <Card
      title={product.name}
      note={
        readiness.data
          ? `${readiness.data.done} z ${readiness.data.total} punktów gotowe`
          : undefined
      }
      action={
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          {product.is_demo && <Chip tone="warn">demo</Chip>}
          <Chip tone={readiness.data?.ready ? "good" : "quiet"}>
            {LABELS.productStatus[product.status] ?? product.status}
          </Chip>
        </div>
      }
    >
      {readiness.isPending ? (
        <Loading label="Sprawdzam konfigurację…" />
      ) : readiness.isError ? (
        <ErrorState error={readiness.error} />
      ) : (
        <ul className="checks">
          {readiness.data?.checks.map((check) => (
            <li key={check.key} className={check.done ? "check check--done" : "check"}>
              <span className="check__mark" aria-hidden="true">
                {check.done ? "✓" : "○"}
              </span>
              <span className="check__body">
                <span className="check__label">{check.label}</span>
                {check.detail && <span className="check__detail mono">{check.detail}</span>}
                {!check.done && check.hint && <span className="check__hint">{check.hint}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      <ProductLabels product={product} />

      <FunnelPages product={product} onSaved={refresh} />

      <ClarityProject product={product} onSaved={refresh} />

      <div className="keys">
        <div className="keys__head">
          <h3>Klucze API</h3>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button
              type="button"
              className="btn"
              disabled={issue.isPending}
              onClick={() => issue.mutate("production")}
            >
              Nowy klucz produkcyjny
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              disabled={issue.isPending}
              onClick={() => issue.mutate("development")}
            >
              deweloperski
            </button>
          </div>
        </div>

        {issued && (
          <div className="secret" role="status">
            <p className="secret__warning">{issued.warning}</p>
            <dl>
              <dt>FOLWARK_HQ_API_KEY</dt>
              <dd className="mono">{issued.secret}</dd>
              <dt>FOLWARK_HQ_SIGNING_SECRET</dt>
              <dd className="mono">{issued.signing_secret}</dd>
            </dl>
            <button type="button" className="btn btn--quiet" onClick={() => setIssued(null)}>
              Zapisałem, ukryj
            </button>
          </div>
        )}

        {keys.isPending ? (
          <Loading label="Wczytuję klucze…" />
        ) : activeKeys.length ? (
          <table className="data">
            <thead>
              <tr>
                <th>Prefiks</th>
                <th>Środowisko</th>
                <th>Ostatnie użycie</th>
                <th>Podpis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {activeKeys.map((key) => (
                <tr key={key.id}>
                  <td className="mono">{key.key_prefix}…</td>
                  <td>{key.environment}</td>
                  <td>{ago(key.last_used_at)}</td>
                  <td
                    title={
                      key.require_signature
                        ? "Żądania bez podpisu HMAC są odrzucane."
                        : "Przyjmuje żądania bez podpisu. Gdy CRM już podpisuje, przełącz poleceniem flask require-signature."
                    }
                  >
                    {key.require_signature ? "wymagany" : "opcjonalny"}
                    {" · "}
                    {key.last_signed_at ? `ostatni ${ago(key.last_signed_at)}` : "brak podpisów"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn btn--quiet"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(key.id)}
                    >
                      Unieważnij
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty
            title="Brak aktywnych kluczy"
            hint="Bez klucza CRM nie ma jak wysłać ani jednego zdarzenia."
          />
        )}
      </div>
    </Card>
  );
}

/* ── Etykiety ─────────────────────────────────────────────────────────────── */

/**
 * Znaczniki, które widać przy produkcie, mają dwa różne źródła i to jest cała
 * trudność tego panelu.
 *
 * „w budowie" to **status** — pole, od którego zależy, czy produkt w ogóle
 * wchodzi do raportów portfolio. „model jednoosobowy" to **deklaracja
 * możliwości** — od niej zależy, czy dashboard pokaże kafelek, którego nie ma
 * jak policzyć. Żadnego z nich nie wolno zamienić w dowolny tekst, bo tekstu
 * nie da się użyć w warunku.
 *
 * Dlatego trzecia sekcja jest osobna: etykiety własne nie znaczą dla systemu
 * nic i właśnie dzięki temu wolno w nich pisać, co się chce.
 */
const CAPABILITY_LABELS: { key: string; label: string; hint: string }[] = [
  {
    key: "accounts",
    label: "Rozróżnia konto od użytkownika",
    hint: "Odznaczone pokazuje przy produkcie znacznik „model jednoosobowy”.",
  },
  {
    key: "trials",
    label: "Ma okres próbny",
    hint: "Odznaczone chowa kafelki konwersji triali — nie ma czego liczyć.",
  },
  {
    key: "free_accounts",
    label: "Zgłasza konta darmowe",
    hint: "Zaznacz dopiero po przeliczeniu historii (flask nightly --full). Odznaczone zostawia etap kont darmowych pusty zamiast zerowego.",
  },
  {
    key: "seats",
    label: "Rozlicza się za stanowiska",
    hint: "Zmienia odczyt podwyżki: więcej stanowisk to nie to samo co droższy plan.",
  },
];

function ProductLabels({ product }: { product: ProductBrief }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  // Etykiety czytamy z produktu, nie z kopii w stanie. Kopia rozjeżdża się
  // w chwili, w której nazwa etykiety zmieni się z drugiej strony ekranu —
  // w karcie „Etykiety w portfolio" — i produkt pokazuje starą nazwę do
  // przeładowania strony.
  const tags = product.tags ?? [];

  const known = useQuery({
    queryKey: ["tags"],
    queryFn: () => api.get<{ tags: PortfolioTag[] }>("/tags"),
  });

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(`/products/${product.slug}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["tags"] });
      void queryClient.invalidateQueries({ queryKey: ["product", product.slug] });
    },
  });

  const addTag = () => {
    const value = draft.trim().replace(/\s+/g, " ");
    if (!value) return;
    if (tags.some((tag) => tag.toLowerCase() === value.toLowerCase())) {
      setDraft("");
      return;
    }
    setDraft("");
    save.mutate({ tags: [...tags, value] });
  };

  const removeTag = (tag: string) => {
    save.mutate({ tags: tags.filter((item) => item !== tag) });
  };

  const suggestions = (known.data?.tags ?? [])
    .map((row) => row.name)
    .filter((name) => !tags.some((tag) => tag.toLowerCase() === name.toLowerCase()));

  return (
    <div className="keys">
      <div className="keys__head">
        <h3>Znaczniki produktu</h3>
        <span className="dim" style={{ fontSize: "0.76rem" }}>
          {save.isPending ? "Zapisuję…" : "zapisuje się od razu"}
        </span>
      </div>

      <div className="labels">
        <label className="labels__field">
          <span className="labels__label">Status</span>
          <select
            value={product.status}
            onChange={(event) => save.mutate({ status: event.target.value })}
          >
            {Object.entries(LABELS.productStatus).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className="labels__help">
            Decyduje o tym, czy produkt wchodzi do raportów portfolio. „archiwum” znika z list.
          </span>
        </label>

        <fieldset className="labels__field">
          <legend className="labels__label">Co produkt potrafi</legend>
          {CAPABILITY_LABELS.map((capability) => (
            <label className="checkbox" key={capability.key}>
              <input
                type="checkbox"
                checked={Boolean(product.capabilities[capability.key])}
                onChange={(event) =>
                  save.mutate({ capabilities: { [capability.key]: event.target.checked } })
                }
              />
              <span>
                {capability.label}
                <small className="labels__help">{capability.hint}</small>
              </span>
            </label>
          ))}
        </fieldset>
      </div>

      <div className="labels__own">
        <p className="labels__label">Etykiety własne</p>
        <ul className="tags-editor">
          {tags.map((tag) => (
            <li key={tag}>
              {tag}
              <button
                type="button"
                aria-label={`Zdejmij etykietę ${tag} z produktu ${product.name}`}
                onClick={() => removeTag(tag)}
              >
                ×
              </button>
            </li>
          ))}
          {!tags.length && <li className="tags-editor__empty">bez etykiet własnych</li>}
        </ul>
        <div className="tags-editor__add">
          <input
            list={`etykiety-${product.slug}`}
            placeholder="nowa etykieta…"
            maxLength={32}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === ",") {
                event.preventDefault();
                addTag();
              }
            }}
            aria-label={`Nowa etykieta produktu ${product.name}`}
          />
          <datalist id={`etykiety-${product.slug}`}>
            {suggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <button type="button" className="btn" onClick={addTag} disabled={!draft.trim()}>
            Dodaj etykietę
          </button>
        </div>
        <p className="labels__help">
          Etykieta nie zmienia żadnej liczby — jest po to, żeby przeglądając portfolio wiedzieć,
          co jest czym. Nazwy porządkuje się w karcie „Etykiety w portfolio”.
        </p>
      </div>

      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}

/**
 * Katalog etykiet całego portfolio.
 *
 * Katalog nie jest osobną tabelą — powstaje z tego, co faktycznie wisi na
 * produktach. Dzięki temu nie da się doprowadzić do stanu, w którym lista zna
 * etykietę, której nikt nie używa. Zmiana nazwy dotyka wszystkich produktów
 * naraz, bo obchodzenie ich po kolei kończy się jednym pominiętym i etykietą,
 * której już nikt nie szuka.
 */
function PortfolioTags() {
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);

  const tags = useQuery({
    queryKey: ["tags"],
    queryFn: () => api.get<{ tags: PortfolioTag[] }>("/tags"),
  });

  const done = () => {
    setRenaming(null);
    setConfirming(null);
    setDraft("");
    void queryClient.invalidateQueries({ queryKey: ["tags"] });
    void queryClient.invalidateQueries({ queryKey: ["products"] });
    void queryClient.invalidateQueries({ queryKey: ["product"] });
  };

  const rename = useMutation({
    mutationFn: (payload: { name: string; rename_to: string }) => api.patch("/tags", payload),
    onSuccess: done,
  });

  const remove = useMutation({
    mutationFn: (name: string) => api.del("/tags", { name }),
    onSuccess: done,
  });

  return (
    <Card
      title="Etykiety w portfolio"
      note="Nazwy własne, którymi opisujesz produkty. Zmiana obejmuje wszystkie produkty naraz."
    >
      {tags.isPending ? (
        <Loading label="Wczytuję etykiety…" />
      ) : tags.isError ? (
        <ErrorState error={tags.error} />
      ) : !tags.data?.tags.length ? (
        <Empty
          title="Nie ma jeszcze żadnej etykiety"
          hint="Dodasz pierwszą niżej, przy produkcie, w sekcji „Znaczniki produktu”."
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Etykieta</th>
                <th>Produkty</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tags.data.tags.map((tag) => (
                <tr key={tag.name}>
                  <td>
                    {renaming === tag.name ? (
                      <input
                        autoFocus
                        maxLength={32}
                        className="tags-editor__rename"
                        value={draft}
                        aria-label={`Nowa nazwa etykiety ${tag.name}`}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && draft.trim()) {
                            rename.mutate({ name: tag.name, rename_to: draft.trim() });
                          }
                          if (event.key === "Escape") setRenaming(null);
                        }}
                      />
                    ) : (
                      <Chip>{tag.name}</Chip>
                    )}
                  </td>
                  <td className="muted">
                    {tag.products.map((entry) => entry.name).join(", ")}{" "}
                    <span className="dim mono">({tag.product_count})</span>
                  </td>
                  <td className="tags-editor__actions">
                    {renaming === tag.name ? (
                      <>
                        <button
                          type="button"
                          className="btn"
                          disabled={!draft.trim() || rename.isPending}
                          onClick={() => rename.mutate({ name: tag.name, rename_to: draft.trim() })}
                        >
                          Zapisz nazwę
                        </button>
                        <button
                          type="button"
                          className="btn btn--quiet"
                          onClick={() => setRenaming(null)}
                        >
                          Anuluj
                        </button>
                      </>
                    ) : confirming === tag.name ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(tag.name)}
                        >
                          Zdejmij z {tag.product_count}{" "}
                          {tag.product_count === 1 ? "produktu" : "produktów"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--quiet"
                          onClick={() => setConfirming(null)}
                        >
                          Anuluj
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn--quiet"
                          onClick={() => {
                            setRenaming(tag.name);
                            setConfirming(null);
                            setDraft(tag.name);
                          }}
                        >
                          Zmień nazwę
                        </button>
                        <button
                          type="button"
                          className="btn btn--quiet"
                          onClick={() => {
                            setConfirming(tag.name);
                            setRenaming(null);
                          }}
                        >
                          Usuń
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rename.isError && <ErrorState error={rename.error} />}
      {remove.isError && <ErrorState error={remove.error} />}
    </Card>
  );
}
